/**
 * Build a typed value suitable for deterministic JSON serialization.
 * @param {*} value query value.
 * @returns {Array} typed serializable query value.
 */
function buildSerializableQueryValue(value) {
  if (Array.isArray(value)) {
    return ['array', value.map(buildSerializableQueryValue)];
  }
  if (value === null) {
    return ['null'];
  }
  if (value === undefined) {
    return ['undefined'];
  }
  return [typeof value, value];
}

/**
 * Get a stable React `key` for a Show route renderer from the route `uuid` and
 * its URL parameters. Locale must not change the key: locale-only replacements
 * deliberately bypass Found route matching and should leave the mounted
 * dashboard and Relay query alive.
 * @param {object} props - Found-relay route props (or a `{ match }` fallback).
 * @param {object} props.match - The matched route object.
 * @param {object} props.match.params - URL parameters from the matched route.
 * @param {string} props.match.params.uuid - The UUID from the route path.
 * @param {object} [props.match.location] - The matched location.
 * @param {object} [props.match.location.query] - Parsed URL query params.
 * @returns {string} A deterministic key combining the route `uuid` and a
 *   stable serialization of the query params.
 */
function getShowRouteKey(props) {
  const { match } = props || {};
  const routeUuid = match?.params?.uuid ?? '';
  const query = match?.location?.query || {};

  const queryEntries = Object.keys(query)
    .filter((queryKey) => queryKey !== 'locale')
    .sort()
    .map((queryKey) => [queryKey, buildSerializableQueryValue(query[queryKey])]);
  const serialisedQuery = queryEntries.length === 0 ? '' : JSON.stringify(queryEntries);

  return `${routeUuid}|${serialisedQuery}`;
}

export default getShowRouteKey;
